import { Category, MILLI, Place, RecordId } from '@itc-mod/supply/shared';
import { z } from 'zod';
import type { DonationStatus, Line } from './schemas.ts';

// Requests (plan 2C Assumption 5) and Sierra's rules for them: progress (R1,
// `reqProgress`, sierra-panel.html line 1884), linking (R2), order (R3, line 1938)
// and transitions (R4). Donation and delivery lines link to request lines by id, so
// progress no longer matches item names (the panel's `sameTxt`).

export const RequestStatus = z.enum(['requested', 'active', 'fulfilled', 'denied']);
export type RequestStatus = z.infer<typeof RequestStatus>;

export const Priority = z.enum(['Low', 'Normal', 'High', 'Urgent']);
export type Priority = z.infer<typeof Priority>;

/** A request line's unit: SCU (in thousandths), a count, or whole aUEC. */
export const RequestUnit = z.enum(['scu', 'units', 'auec']);
export type RequestUnit = z.infer<typeof RequestUnit>;

export const RequestLine = z.object({
  id: z.string(),
  name: z.string(),
  /** The line's own category, which "Log donation" prefills (the panel guessed it from the name). Null for aUEC. */
  category: Category.nullable(),
  unit: RequestUnit,
  quantity: z.number().int(),
});
export type RequestLine = z.infer<typeof RequestLine>;

export const Request = z.object({
  id: z.string(),
  title: z.string(),
  lines: z.array(RequestLine),
  requester: z.string(),
  deliverTo: Place.nullable(),
  neededBy: z.iso.datetime().nullable(),
  priority: Priority,
  status: RequestStatus,
  contact: z.string(),
  notes: z.string(),
  /** Why it was denied: the requester sees it. */
  reason: z.string(),
  postedAt: z.iso.datetime().nullable(),
  fulfilledAt: z.iso.datetime().nullable(),
  created: z.iso.datetime(),
  version: z.number().int(),
});
export type Request = z.infer<typeof Request>;

/** R1, one request line: what's been received (got), what's promised but not here (pledged), what's left, and what nobody has promised (open). */
export const LineProgress = z.object({ lineId: z.string(), got: z.number().int(), pledged: z.number().int(), left: z.number().int(), open: z.number().int() });
export type LineProgress = z.infer<typeof LineProgress>;

/** A request with its progress: totals per line, never who gave what. */
export const RequestView = z.object({ request: Request, progress: z.array(LineProgress) });
export type RequestView = z.infer<typeof RequestView>;

// Inputs.

export const RequestLineInput = z
  .object({
    id: z.string().max(32).nullable().default(null),
    name: z.string().trim().min(1).max(80),
    category: Category.nullable().default(null),
    unit: RequestUnit,
    quantity: z.number().int().min(0).max(1e12),
  })
  .transform((line) => ({ ...line, category: line.unit === 'auec' ? null : line.category }));
export type RequestLineInput = z.input<typeof RequestLineInput>;

const RequestFields = z.object({
  title: z.string().trim().min(1).max(120),
  lines: z.array(RequestLineInput).min(1).max(30),
  deliverTo: Place.nullable().default(null),
  neededBy: z.iso.datetime().nullable().default(null),
  priority: Priority.default('Normal'),
  contact: z.string().trim().max(100).default(''),
  notes: z.string().trim().max(2000).default(''),
});

/** A new request. Members submit drafts (`requested`); coordinators may create it Active. */
export const RequestInput = RequestFields.extend({ status: z.enum(['requested', 'active']).default('requested') });
export type RequestInput = z.input<typeof RequestInput>;

export const RequestUpdate = RequestFields.extend({ version: z.number().int().min(1) });
export type RequestUpdate = z.input<typeof RequestUpdate>;

/** "Copy for Discord" in the post sheet stamps posted_at, and activates a draft if "Mark Active" is ticked. */
export const PostInput = z.object({ version: z.number().int().min(1), markActive: z.boolean().default(false) });

// R1.

/** A line linked to a request line, from a donation or a delivery, with the state that decides how it counts. */
export interface Contribution {
  /** The request line it links to. */
  requestLine: string;
  /** In the request line's unit (see `contribution`). */
  amount: number;
  /** Accepted donations and received deliveries are got; open donations and expected deliveries are pledged; the rest count for nothing. */
  state: 'got' | 'pledged' | 'none';
}

/** How a donation counts towards its request (R1, Assumption 7). */
export function donationState(status: DonationStatus): Contribution['state'] {
  if (status === 'accepted') return 'got';
  if (status === 'pledged' || status === 'handed_over') return 'pledged';
  return 'none';
}

/** How a delivery counts: received is got (port: the panel counted nothing, handoff bug 5), expected is pledged. */
export function deliveryState(status: 'expected' | 'received' | 'cancelled'): Contribution['state'] {
  return status === 'received' ? 'got' : status === 'expected' ? 'pledged' : 'none';
}

/** What one item line adds to a request line of `unit`: SCU lines and crates (count × size) to SCU; counted units and kits to units. */
export function contribution(unit: RequestUnit, line: Pick<Line, 'unit' | 'crateSize' | 'quantity'>): number {
  if (unit === 'scu') return line.unit === 'scu' ? line.quantity : line.unit === 'crates' ? line.quantity * (line.crateSize ?? 0) * MILLI : 0;
  if (unit === 'units') return line.unit === 'units' || line.unit === 'kits' ? line.quantity : 0;
  return 0;
}

/** R1: each request line's got, pledged, left and open, from the contributions linked to it. */
export function progress(lines: readonly Pick<RequestLine, 'id' | 'quantity'>[], contributions: readonly Contribution[]): LineProgress[] {
  return lines.map((line) => {
    const mine = contributions.filter((entry) => entry.requestLine === line.id);
    const got = mine.filter((entry) => entry.state === 'got').reduce((sum, entry) => sum + entry.amount, 0);
    const pledgedSum = mine.filter((entry) => entry.state === 'pledged').reduce((sum, entry) => sum + entry.amount, 0);
    const left = Math.max(0, line.quantity - got);
    return { lineId: line.id, got, pledged: Math.min(pledgedSum, left), left, open: Math.max(0, left - pledgedSum) };
  });
}

// R2.

/** R2: may a donation line link to this request line? Null if so; the reason if not. */
export function linkRefusal(requestLine: Pick<RequestLine, 'unit'>, line: { kind: 'auec' } | { kind: 'items'; unit: Line['unit'] }): string | null {
  if (requestLine.unit === 'auec') return line.kind === 'auec' ? null : 'That request line is aUEC: give aUEC as its own donation';
  if (line.kind === 'auec') return 'That request line is for items, not aUEC';
  if (requestLine.unit === 'scu') return line.unit === 'scu' || line.unit === 'crates' ? null : 'That line is counted, and the request is in SCU';
  return line.unit === 'units' || line.unit === 'kits' ? null : 'That line is in SCU, and the request is counted';
}

/** R2's last row: only an Active request takes new links, unless it's the donation's own already. */
export function requestLinkRefusal(status: RequestStatus, alreadyLinked: boolean): string | null {
  return status === 'active' || alreadyLinked ? null : 'Only an active request takes donations';
}

// R3.

const STATUS_ORDER: readonly RequestStatus[] = ['requested', 'active', 'fulfilled', 'denied'];
const PRIORITY_ORDER: readonly Priority[] = ['Urgent', 'High', 'Normal', 'Low'];

/** By priority, Urgent first, then needed by, with none last. */
export function byUrgency(a: Pick<Request, 'priority' | 'neededBy'>, b: Pick<Request, 'priority' | 'neededBy'>): number {
  const priority = PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority);
  if (priority !== 0) return priority;
  const at = (request: Pick<Request, 'neededBy'>) => (request.neededBy === null ? Number.POSITIVE_INFINITY : Date.parse(request.neededBy));
  return at(a) - at(b) || 0;
}

/** R3: status (Requested, Active, Fulfilled, Denied), then urgency. */
export function requestOrder(a: Pick<Request, 'status' | 'priority' | 'neededBy'>, b: Pick<Request, 'status' | 'priority' | 'neededBy'>): number {
  return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || byUrgency(a, b);
}

/** `genRequests` and the Requests widget: Active only, by urgency. */
export function activeByUrgency<R extends Pick<Request, 'status' | 'priority' | 'neededBy'>>(requests: readonly R[]): R[] {
  return requests.filter((request) => request.status === 'active').sort(byUrgency);
}

// R4.

export type RequestAction = 'activate' | 'deny' | 'fulfil' | 'reopen' | 'edit' | 'withdraw';

const FROM: Readonly<Record<RequestAction, readonly RequestStatus[]>> = {
  activate: ['requested'],
  deny: ['requested', 'active'],
  fulfil: ['active'],
  reopen: ['fulfilled', 'denied'],
  edit: ['requested', 'active'],
  withdraw: ['requested'],
};

const TO: Readonly<Record<RequestAction, RequestStatus | null>> = { activate: 'active', deny: 'denied', fulfil: 'fulfilled', reopen: 'active', edit: null, withdraw: null };

const REFUSED: Readonly<Record<RequestAction, string>> = {
  activate: 'Only a draft can be activated',
  deny: 'Only a draft or an active request can be denied',
  fulfil: 'Only an active request can be fulfilled',
  reopen: 'Only a fulfilled or denied request can be reopened',
  edit: 'A fulfilled or denied request is reopened before it changes',
  withdraw: 'Only a draft can be withdrawn',
};

/** R4: where an action takes a request, the same status for an edit, null for a withdrawn (deleted) draft, or a refusal. */
export function requestTransition(status: RequestStatus, action: RequestAction): { ok: true; to: RequestStatus | null } | { ok: false; message: string } {
  if (!FROM[action].includes(status)) return { ok: false, message: REFUSED[action] };
  return { ok: true, to: action === 'edit' ? status : TO[action] };
}

/** R4's "who": coordinators do anything R4 allows; a requester only edits or withdraws their own draft. */
export function requestActorRefusal(action: RequestAction, actor: { userId: string; coordinator: boolean }, request: Pick<Request, 'requester' | 'status'>): string | null {
  if (actor.coordinator) return null;
  if ((action === 'edit' || action === 'withdraw') && request.requester === actor.userId && request.status === 'requested') return null;
  return action === 'edit' || action === 'withdraw' ? 'Only its requester, while it’s a draft, or a coordinator can change this request' : 'Only a coordinator can do that';
}

export { RecordId };
